/**
 * Post FAQs Page
 * Review-only FAQ view for HTML/no-CMS clients. FAQs are generated automatically during
 * analysis (same pipeline as HubSpot) but there is no CMS to inject them into, so this
 * page omits the schema/injection controls shown in the HubSpot Semantic Schema > FAQ
 * Review tab and only surfaces the extracted questions/answers per post, plus the minimal
 * settings needed to control generation (enable + max per post).
 */

import { useState, useEffect, useCallback } from 'react';
import { HelpCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { schemaApi, type SchemaOrganization, type SchemaAuthor, type SchemaProduct, type SchemaOffer } from '../../lib/dashboard-api';
import { FaqReviewPanel } from '../../components/schema/FaqReviewPanel';
import { Section, Field } from '../../components/schema/SchemaFormControls';

const inputCls = 'w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#ffdc18]/50';

export default function PostFaqsPage() {
  const { currentSite } = useAuth();
  const siteId = currentSite?.id;

  const [loading, setLoading] = useState(true);
  const [enableFaqExtraction, setEnableFaqExtraction] = useState(true);
  const [maxFaqsPerPost, setMaxFaqsPerPost] = useState(5);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Pass-through fields we never edit here but must not wipe on save.
  const [passThrough, setPassThrough] = useState<{
    organization: SchemaOrganization | null;
    authors: SchemaAuthor[];
    products: SchemaProduct[];
    offers: SchemaOffer[];
    vizzexBrandLink: boolean;
  }>({ organization: null, authors: [], products: [], offers: [], vizzexBrandLink: false });

  const loadSettings = useCallback(() => {
    if (!siteId) return;
    setLoading(true);
    schemaApi.getEntities(siteId)
      .then(data => {
        setEnableFaqExtraction(data.faqSettings?.enableFaqExtraction ?? true);
        setMaxFaqsPerPost(data.faqSettings?.maxFaqsPerPost ?? 5);
        setPassThrough({
          organization: data.organization,
          authors: data.authors,
          products: data.products,
          offers: data.offers,
          vizzexBrandLink: data.vizzexBrandLink ?? false,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    setMessage(null);
    try {
      await schemaApi.saveAndSyncEntities(siteId, {
        ...passThrough,
        enableFaqExtraction,
        maxFaqsPerPost,
      });
      setMessage({ type: 'success', text: 'FAQ settings saved and synced.' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save FAQ settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-64 text-white/60">
        No site selected.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#ffdc18]/10 flex items-center justify-center">
          <HelpCircle className="w-5 h-5 text-[#ffdc18]" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Post FAQs</h1>
          <p className="text-sm text-white/60">FAQs automatically extracted from your posts during analysis</p>
        </div>
      </div>

      {/* FAQ Settings */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
      ) : (
        <Section
          title="FAQ Settings"
          icon={<HelpCircle className="w-4 h-4" />}
          action={
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 text-white/60" />}
              {saving ? 'Saving…' : 'Save FAQ Settings'}
            </button>
          }
        >
          <div className="flex items-center justify-between mb-3 max-w-md">
            <span className="text-sm">Enable FAQ extraction from posts</span>
            <button
              onClick={() => setEnableFaqExtraction(v => !v)}
              className={`relative w-10 h-5 rounded-full transition-colors ${enableFaqExtraction ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enableFaqExtraction ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          <Field label="Max FAQs per post" className="max-w-[10rem]">
            <input
              type="number" min={1} max={20}
              value={maxFaqsPerPost}
              onChange={e => setMaxFaqsPerPost(parseInt(e.target.value) || 5)}
              className={inputCls}
            />
          </Field>

          {message && (
            <p className={`mt-3 text-sm ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {message.text}
            </p>
          )}
        </Section>
      )}

      <FaqReviewPanel siteId={siteId} showSchemaToggle={false} />
    </div>
  );
}
